/**
 * Client wrappers for `/api/order-units/*`. wrapError surfaces `templateCount`
 * from 409 responses so the UI can show "this unit has N drawings, delete those first".
 */

import axios from 'axios';
import { buildApiUrl, tokenManager } from '../config/api.config';
import { logger } from '../utils/logger';

const authHeaders = () => {
  const token = tokenManager.getToken();
  if (!token) throw new Error('Authentication token not found');
  return {
    'x-access-token': token,
    Accept: 'application/json',
    'Content-Type': 'application/json'
  };
};

const wrapError = (error, context) => {
  logger.error(`[orderUnitApi] ${context} failed`, error);
  if (error.response) {
    const data = error.response.data || {};
    return {
      success: false,
      status: error.response.status,
      error: data.error || context + ' failed',
      message: data.message,
      templateCount: data.templateCount,
      details: data.details
    };
  }
  if (error.request) {
    return { success: false, error: 'Network error', message: 'No response from server.' };
  }
  return { success: false, error: error.message };
};

export const createUnits = async (orderId, units, createdBy = null) => {
  try {
    const url = buildApiUrl(`api/order-units/${orderId}/bulk`);
    const response = await axios.post(url, { units, created_by: createdBy }, { headers: authHeaders() });
    return { success: true, units: response.data.units };
  } catch (error) {
    return wrapError(error, 'createUnits');
  }
};

export const listUnits = async (orderId, { includeDeleted = false } = {}) => {
  try {
    const url = buildApiUrl(`api/order-units/${orderId}${includeDeleted ? '?includeDeleted=true' : ''}`);
    const response = await axios.get(url, { headers: authHeaders() });
    return { success: true, units: response.data.units || [] };
  } catch (error) {
    return wrapError(error, 'listUnits');
  }
};

export const getUnitByNumber = async (unitOrderNumber) => {
  try {
    const url = buildApiUrl(`api/order-units/by-unit-number/${encodeURIComponent(unitOrderNumber)}`);
    const response = await axios.get(url, { headers: authHeaders() });
    return { success: true, unit: response.data.unit };
  } catch (error) {
    return wrapError(error, 'getUnitByNumber');
  }
};

// Ordinals never reuse — server picks the next available, even past previously-deleted ones.
export const addMoreUnits = async (orderId, count, createdBy = null) => {
  try {
    const url = buildApiUrl(`api/order-units/${orderId}/append`);
    const response = await axios.post(url, { count, created_by: createdBy }, { headers: authHeaders() });
    return { success: true, units: response.data.units };
  } catch (error) {
    return wrapError(error, 'addMoreUnits');
  }
};

// Renames the unit's suffix. Server recomputes unit_order_number from the
// order's order_unique_id; unit_ordinal is preserved so future appends keep
// advancing past this slot.
export const renameUnit = async (unitId, newSuffix, updatedBy = null) => {
  try {
    const url = buildApiUrl(`api/order-units/${unitId}`);
    const response = await axios.patch(
      url,
      { unit_id: newSuffix, updated_by: updatedBy },
      { headers: authHeaders() }
    );
    return { success: true, unit: response.data.unit };
  } catch (error) {
    return wrapError(error, 'renameUnit');
  }
};

// Server returns 409 + { templateCount } if drawings still reference the unit's suffix.
export const deleteUnit = async (unitId, updatedBy = null) => {
  try {
    const url = buildApiUrl(`api/order-units/${unitId}`);
    const response = await axios.delete(url, {
      headers: authHeaders(),
      data: { updated_by: updatedBy }
    });
    return { success: true, unit: response.data.unit };
  } catch (error) {
    return wrapError(error, 'deleteUnit');
  }
};

// Assigns the unit to the calling user (server reads user_ref_id from JWT).
// Server returns 409 if the unit is already claimed by another designer.
export const assignUnitToSelf = async (unitId) => {
  try {
    const url = buildApiUrl(`api/order-units/${unitId}/assign`);
    const response = await axios.post(url, {}, { headers: authHeaders() });
    return { success: true, unit: response.data.unit };
  } catch (error) {
    return wrapError(error, 'assignUnitToSelf');
  }
};

// Designer hand-off: marks the unit finished and ready for QC. Server reads
// the owning designer from the JWT — returns 403 if the unit is not yours,
// 409 if it has no drawings yet.
export const moveUnitToQC = async (unitId) => {
  try {
    const url = buildApiUrl(`api/order-units/${unitId}/move-to-qc`);
    const response = await axios.post(url, {}, { headers: authHeaders() });
    return { success: true, unit: response.data.unit };
  } catch (error) {
    return wrapError(error, 'moveUnitToQC');
  }
};

// QC self-assign: the calling user claims the unit for QC (server reads
// user_ref_id from JWT). Server returns 409 if the unit is not yet ready for
// QC or is already claimed by another QC.
export const assignUnitToQC = async (unitId) => {
  try {
    const url = buildApiUrl(`api/order-units/${unitId}/assign-qc`);
    const response = await axios.post(url, {}, { headers: authHeaders() });
    return { success: true, unit: response.data.unit };
  } catch (error) {
    return wrapError(error, 'assignUnitToQC');
  }
};

// QC clears the unit for production (mirrors the order-level "Move to
// Production" action). Server returns 403 if the unit is not assigned to
// you for QC, 409 if it is not currently in QC.
export const qcPassUnit = async (unitId) => {
  try {
    const url = buildApiUrl(`api/order-units/${unitId}/qc-pass`);
    const response = await axios.post(url, {}, { headers: authHeaders() });
    return { success: true, unit: response.data.unit };
  } catch (error) {
    return wrapError(error, 'qcPassUnit');
  }
};

export const assignToStartProductionPerUnit = async (
  unitId,
  { orderStatus, rackId = null, dimension1 = null, dimension2 = null }
) => {
  try {
    const params = new URLSearchParams({ prodStatus: String(orderStatus) });
    if (rackId) params.append('rackid', rackId);
    if (dimension1 != null) params.append('dimensionOne', dimension1);
    if (dimension2 != null) params.append('dimensionTwo', dimension2);
    const url = buildApiUrl(`api/order-units/${unitId}/assign-production?${params.toString()}`);
    const response = await axios.post(url, null, { headers: authHeaders() });
    return { success: true, unit: response.data };
  } catch (error) {
    return wrapError(error, 'assignToStartProductionPerUnit');
  }
};

// FormData must include barcode image files under field name "documents".
export const sentBarcodeDesignsForPrintingPerUnit = async (unitId, formData) => {
  try {
    const token = tokenManager.getToken();
    if (!token) throw new Error('Authentication token not found');
    const url = buildApiUrl(`api/order-units/${unitId}/sent-barcode-designs`);
    const response = await axios.post(url, formData, {
      headers: {
        'x-access-token': token,
        Accept: 'application/json'
      }
    });
    return { success: true, unit: response.data.unit, uploadedCount: response.data.uploadedCount };
  } catch (error) {
    return wrapError(error, 'sentBarcodeDesignsForPrintingPerUnit');
  }
};

export default {
  createUnits,
  listUnits,
  getUnitByNumber,
  addMoreUnits,
  renameUnit,
  deleteUnit,
  assignUnitToSelf,
  moveUnitToQC,
  assignUnitToQC,
  qcPassUnit,
  assignToStartProductionPerUnit,
  sentBarcodeDesignsForPrintingPerUnit
};
